import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AssignmentTurnedIn as AssignmentTurnedInIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { DASHBOARD_REMINDERS_CHANGED_EVENT } from '../utils/dashboardReminders';

interface RecurringTaskRow {
  id: number;
  title: string;
  detail: string;
  due_on: string;
  deadline_on: string;
  period_label: string;
  status: 'open' | 'done';
  assignee: 'you' | 'all';
}

function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[month - 1]} ${year}`;
}

const RecurringTasksCard: React.FC = () => {
  const [tasks, setTasks] = useState<RecurringTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [intervalMonths, setIntervalMonths] = useState('1');
  const [anchorDueOn, setAnchorDueOn] = useState('');
  const [periodMonths, setPeriodMonths] = useState('0');
  const [assignee, setAssignee] = useState<'me' | 'all'>('me');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await api.get('/api/recurring-tasks');
      setTasks(Array.isArray(response.data?.tasks) ? response.data.tasks : []);
    } catch {
      setError('Could not load recurring tasks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const raw = searchParams.get('complete_task');
    const taskId = Number(raw);
    if (!raw || !Number.isInteger(taskId) || taskId <= 0) {
      if (window.location.hash === '#recurring-tasks') {
        document.getElementById('recurring-tasks')?.scrollIntoView({ block: 'start' });
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/api/recurring-tasks/${taskId}/complete`);
        if (cancelled) return;
        await load();
        window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
      } catch {
        if (!cancelled) setError('Could not mark that task done.');
      } finally {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams);
        next.delete('complete_task');
        const search = next.toString();
        navigate(
          {
            pathname: window.location.pathname,
            search: search ? `?${search}` : '',
            hash: '#recurring-tasks',
          },
          { replace: true }
        );
        document.getElementById('recurring-tasks')?.scrollIntoView({ block: 'start' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, load]);

  const toggle = async (task: RecurringTaskRow) => {
    setError(null);
    const path =
      task.status === 'open'
        ? `/api/recurring-tasks/${task.id}/complete`
        : `/api/recurring-tasks/${task.id}/reopen`;
    try {
      await api.post(path);
      await load();
      window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
    } catch {
      setError('Could not update that task.');
    }
  };

  const dismiss = async (task: RecurringTaskRow) => {
    setError(null);
    try {
      await api.post(`/api/recurring-tasks/${task.id}/dismiss`);
      await load();
      window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
    } catch {
      setError('Could not dismiss that task.');
    }
  };

  const submitRule = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/api/recurring-tasks/rules', {
        title: title.trim(),
        detail: detail.trim(),
        interval_months: Number(intervalMonths),
        anchor_due_on: anchorDueOn,
        period_months: Number(periodMonths),
        assignee,
      });
      setModalOpen(false);
      setTitle('');
      setDetail('');
      setIntervalMonths('1');
      setAnchorDueOn('');
      setPeriodMonths('0');
      setAssignee('me');
      await load();
      window.dispatchEvent(new CustomEvent(DASHBOARD_REMINDERS_CHANGED_EVENT));
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not add that recurring task.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Accordion
      id="recurring-tasks"
      data-testid="recurring-tasks-card"
      defaultExpanded
      disableGutters
      variant="outlined"
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="recurring-tasks-content"
        id="recurring-tasks-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
          <AssignmentTurnedInIcon color="primary" />
          <Typography variant="h6">Recurring Tasks</Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ ml: 'auto' }}
            onClick={(event) => {
              event.stopPropagation();
              setModalOpen(true);
            }}
            onFocus={(event) => event.stopPropagation()}
          >
            Add Recurring Task
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails id="recurring-tasks-content">
        {error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading tasks…
          </Typography>
        ) : tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recurring tasks due. Add one, or they appear here on the day they are due.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {tasks.map((task) => (
              <Box
                key={task.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: task.status === 'done' ? 'success.light' : 'divider',
                }}
              >
                <Checkbox
                  checked={task.status === 'done'}
                  onChange={() => void toggle(task)}
                  inputProps={{ 'aria-label': `Mark done: ${task.title}` }}
                  sx={{ pt: 0.25 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textDecoration: task.status === 'done' ? 'line-through' : 'none',
                    }}
                  >
                    {task.title}
                  </Typography>
                  {task.detail ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {task.detail}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {task.period_label ? `Period ${task.period_label} · ` : ''}
                    Due {formatIsoDate(task.due_on)} · Deadline {formatIsoDate(task.deadline_on)} ·{' '}
                    {task.assignee === 'all' ? 'All' : 'You'}
                  </Typography>
                </Box>
                {task.status === 'open' ? (
                  <Button
                    size="small"
                    onClick={() => void dismiss(task)}
                    aria-label={`Dismiss: ${task.title}`}
                    sx={{
                      ml: 'auto',
                      flexShrink: 0,
                      alignSelf: 'center',
                      textTransform: 'none',
                      color: 'text.secondary',
                    }}
                  >
                    Dismiss
                  </Button>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Recurring Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Details"
              value={detail}
              onChange={(event) => setDetail(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Repeats every (months)"
              type="number"
              value={intervalMonths}
              onChange={(event) => setIntervalMonths(event.target.value)}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              label="First due date"
              type="date"
              value={anchorDueOn}
              onChange={(event) => setAnchorDueOn(event.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="Months covered before the due date"
              type="number"
              value={periodMonths}
              onChange={(event) => setPeriodMonths(event.target.value)}
              inputProps={{ min: 0 }}
              helperText="Use 0 when the task is just due that day. The deadline is the last day of the due month."
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="recurring-task-assignee-label">Who can complete it</InputLabel>
              <Select
                labelId="recurring-task-assignee-label"
                label="Who can complete it"
                value={assignee}
                onChange={(event) => setAssignee(event.target.value as 'me' | 'all')}
              >
                <MenuItem value="me">Only me</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !title.trim() || !anchorDueOn}
            onClick={() => void submitRule()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RecurringTasksCard;
